/**
 * Trial mode: the whole Fuel loop on one device, no backend.
 *
 * Deliberately its own route with its own storage (`localStore.js`), so the
 * real, server-authoritative path stays exactly as it is. What's shared is the
 * domain module and the components — which is the point, because a week of
 * carrying this around only tells you something if it's the real arithmetic
 * and the real screens.
 */

import { useEffect, useState } from 'react';
import FuelGauge from '../ui/FuelGauge.jsx';
import QuickLog from '../ui/QuickLog.jsx';
import { FuelSetupForm } from './FuelSetup.jsx';
import AskFlow from '../ui/AskFlow.jsx';
import ProposalQueue from '../ui/ProposalQueue.jsx';
import History from '../ui/History.jsx';
import {
  approveProposal, createProposal, declineProposal,
  envelopeReversalEntry, projectEnvelope, projectGoal, spendEntry,
} from '../domain/envelope.js';
import { centsFromDollars, formatCents } from '../domain/money.js';
import { toDateOnly } from '../domain/periods.js';
import * as store from '../lib/localStore.js';
import { Sheet } from './FridgeSheet.jsx';

const today = () => toDateOnly(new Date());

/**
 * Naming a goal after the fact.
 *
 * The setup form says the goal is optional and can be added later, which was a
 * promise nothing kept. It matters more than an ordinary loose end: rollover is
 * the half of this feature that makes restraint visibly productive, and without
 * a goal there is nowhere for unspent fuel to go, so the gauge only ever
 * measures what the family didn't have.
 */
function AddGoal({ onAdd, balanceCents }) {
  const [name, setName] = useState('');
  const [dollars, setDollars] = useState('300');

  return (
    <form
      className="card stack"
      onSubmit={(e) => { e.preventDefault(); if (name.trim()) onAdd(name.trim(), centsFromDollars(dollars)); }}
    >
      <p className="eyebrow">Nothing to save toward yet</p>
      <p className="small muted">
        Whatever’s left at the end of the week goes here. It’s the part that makes skipping
        the ice cream feel like something rather than nothing.
      </p>
      {balanceCents > 0 && (
        <p className="notice info">
          Heads up: the {formatCents(balanceCents)} already in the tank will go to this at the
          end of the week, along with anything else you don’t spend.
        </p>
      )}
      <div className="row">
        <label style={{ flex: '2 1 10rem' }}>
          Goal
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Beach trip" />
        </label>
        <label style={{ flex: '1 1 7rem' }}>
          Target in dollars
          <input
            type="number" inputMode="decimal" min="1" step="1"
            value={dollars} onChange={(e) => setDollars(e.target.value)}
          />
        </label>
      </div>
      <button className="primary" type="submit" disabled={!name.trim()}>Save it</button>
    </form>
  );
}

export default function Try() {
  const [state, setState] = useState(null);
  const [ready, setReady] = useState(false);
  // One device, two people. Not a security boundary — it is a shared tablet on
  // a kitchen table, and pretending otherwise would mean building child
  // accounts, which is exactly what this product doesn't do.
  const [kidView, setKidView] = useState(false);
  // Its own flag rather than a route: `/try` is the only path this app
  // handles pre-auth (see App.jsx), and a query-string round trip would add a
  // second thing to keep in sync with `store` for no benefit — this is a
  // single device with nothing to link to anyway.
  const [printing, setPrinting] = useState(false);

  // Catch up on every open, exactly like the real app will call the job.
  useEffect(() => {
    const loaded = store.read();
    setState(loaded ? store.catchUp(loaded) : null);
    setReady(true);
  }, []);

  if (!ready) return <main className="app-shell"><p className="muted">Loading…</p></main>;

  if (!state) {
    return (
      <main className="app-shell stack">
        <p className="eyebrow">Fortunaut · on this device</p>
        <h1>Fill the tank</h1>
        <p className="muted">
          One pot for the fun stuff, topped up on a schedule. Everything stays on this
          phone — no account, nothing sent anywhere.
        </p>
        <FuelSetupForm
          busy={false}
          error={null}
          // Catch up immediately, or a brand-new tank reads $0.00 until the
          // next time the app is opened — the refill for the period that has
          // already started is owed the moment the rule exists.
          onSubmit={(values) => setState(store.catchUp(store.create({
            rule: {
              version: 'fortunaut-envelope-v1',
              cadence: values.cadence,
              amountCents: values.amountCents,
              rolloverCapCents: values.rolloverCapCents,
            },
            tiles: values.tiles,
            goal: values.goal,
            startedOn: today(),
          })))}
        />
      </main>
    );
  }

  const projection = projectEnvelope(state.entries);
  const goal = state.goal && {
    ...state.goal,
    ...projectGoal(state.entries, state.goal.id),
  };

  if (printing) {
    return (
      <main className="app-shell stack">
        <div className="fridge-sheet__actions">
          <button className="quiet" onClick={() => setPrinting(false)}>Done</button>
          <button className="primary" onClick={() => window.print()}>Print</button>
        </div>
        <Sheet
          familyName="This family"
          kids={[]}
          rule={state.rule}
          tiles={state.tiles}
          goal={state.goal}
          balanceCents={state.rule.amountCents}
        />
      </main>
    );
  }

  return (
    <main className="app-shell stack">
      <div className="spread">
        <p className="eyebrow">Fortunaut · on this device</p>
        <button className="quiet" onClick={() => setKidView((v) => !v)}>
          {kidView ? 'Grown-up view' : 'Kids’ view'}
        </button>
      </div>

      <FuelGauge
        balanceCents={projection.balanceCents}
        refillCents={state.rule.amountCents}
        cadence={state.rule.cadence}
        today={today()}
        goal={goal}
        rolledOverCents={projection.rolledOverCents}
      />

      {kidView ? (
        <AskFlow
          tiles={state.tiles}
          balanceCents={projection.balanceCents}
          goal={goal}
          refillCents={state.rule.amountCents}
          cadence={state.rule.cadence}
          onAsk={({ amountCents, tileId, note }) => setState(store.putProposal(state, createProposal({
            id: crypto.randomUUID(),
            familyId: store.FAMILY_ID,
            envelopeId: store.ENVELOPE_ID,
            childId: 'kid',
            amountCents,
            tileId,
            note,
            // Off by default — see the note on createProposal. A trial has no
            // sibling to second anything anyway.
            requiresSecond: false,
          })))}
        />
      ) : (
      <QuickLog
        tiles={state.tiles}
        // `tile` is null for a one-off logged through "Something else"; the
        // note is what names it in the history afterwards.
        onLog={async (tile, amountCents, note) => {
          const entry = spendEntry({
            familyId: store.FAMILY_ID,
            envelopeId: store.ENVELOPE_ID,
            spendId: crypto.randomUUID(),
            amountCents,
            tileId: tile?.id ?? null,
            note,
            occurredAt: today(),
          });
          setState(store.append(state, entry));
          return entry;
        }}
        onUndo={async (entry) => {
          setState(store.append(state, envelopeReversalEntry({
            original: entry,
            actorId: 'me',
            reason: 'Logged by mistake',
            occurredAt: today(),
          })));
        }}
      />
      )}

      {!kidView && (
        <ProposalQueue
          proposals={state.proposals ?? []}
          tiles={state.tiles}
          balanceCents={projection.balanceCents}
          goal={goal}
          refillCents={state.rule.amountCents}
          cadence={state.rule.cadence}
          onApprove={(p) => {
            // Approving is what spends the money: one entry, keyed on the
            // proposal, so approving twice cannot pay twice.
            const withEntry = store.append(state, spendEntry({
              familyId: store.FAMILY_ID,
              envelopeId: store.ENVELOPE_ID,
              spendId: p.id,
              amountCents: p.amountCents,
              tileId: p.tileId,
              note: p.note,
              occurredAt: today(),
            }));
            setState(store.putProposal(withEntry, approveProposal(p, 'me')));
          }}
          onDecline={(p) => setState(store.putProposal(
            state, declineProposal(p, 'me', 'Not this time'),
          ))}
        />
      )}

      {!state.goal && (
        <AddGoal
          balanceCents={projection.balanceCents}
          onAdd={(name, targetCents) => {
            // `goalFromDate` keeps rollovers from being dated before the goal
            // existed. It does not ring-fence the money already banked — the
            // card above says so plainly rather than letting a parent discover
            // it when the tank empties.
            const next = store.write({
              ...state,
              goal: { id: 'goal-1', name, targetCents, startedOn: today() },
            });
            setState(store.catchUp(next));
          }}
        />
      )}

      <History entries={state.entries} tiles={state.tiles} goalName={state.goal?.name} />

      {!kidView && (
        <button className="quiet" onClick={() => setPrinting(true)}>Print a fridge sheet</button>
      )}

      <details className="card">
        <summary className="small muted">About this trial</summary>
        <div className="stack tight" style={{ paddingTop: 'var(--space-3)' }}>
          <p className="small muted">
            Everything lives in this browser’s storage. Clearing site data clears the tank,
            and it doesn’t sync between devices — that needs the real backend.
          </p>
          <p className="small muted">
            Started {state.startedOn} · {state.entries.length} entries ·{' '}
            refills {state.rule.cadence} at {formatCents(state.rule.amountCents)}
          </p>
          <button
            className="quiet"
            onClick={() => {
              if (confirm('Clear the tank and start over? This cannot be undone.')) {
                store.clear();
                setState(null);
              }
            }}
          >
            Start over
          </button>
        </div>
      </details>
    </main>
  );
}
